import type { ButtonHTMLAttributes, ReactNode } from "react";

type Variant = "primary" | "secondary" | "ghost" | "danger";
type Size = "sm" | "md" | "lg";
/** docs/06 §4.1 v2 — controle (retângulo, seriedade) × convite (pílula, o gesto dela). */
type Shape = "control" | "invite";

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  shape?: Shape;
  loading?: boolean;
  fullWidth?: boolean;
  children: ReactNode;
}

/** Altura mínima de 44px em md/lg — exigência de área de toque do iOS (RNF-09). */
const SIZE_CLASSES: Record<Size, string> = {
  sm: "h-9 px-4 text-[13px]",
  md: "h-11 px-5 text-[15px]",
  lg: "h-14 px-6 text-[18px]",
};

const SHAPE_CLASSES: Record<Shape, string> = {
  control: "rounded-[var(--radius-control)]",
  invite: "rounded-[var(--radius-pill)]",
};

const VARIANT_CLASSES: Record<Variant, string> = {
  primary:
    "liquid-glass-brand hover:brightness-110 active:brightness-95 active:translate-y-px",
  secondary:
    "bg-transparent text-text-primary border border-border-default " +
    "hover:border-border-strong hover:bg-[var(--accent-soft)]",
  ghost: "bg-transparent text-text-secondary hover:text-text-primary hover:bg-[var(--accent-soft)]",
  danger:
    "bg-transparent text-danger border border-[color-mix(in_srgb,var(--danger)_40%,transparent)] " +
    "hover:bg-[var(--danger-soft)]",
};

export function Button({
  variant = "primary",
  size = "md",
  shape = "control",
  loading = false,
  fullWidth = false,
  disabled,
  className = "",
  children,
  ...rest
}: ButtonProps) {
  const isDisabled = disabled === true || loading;

  return (
    <button
      {...rest}
      disabled={isDisabled}
      aria-busy={loading}
      className={[
        "inline-flex items-center justify-center gap-2 font-medium",
        "transition-all duration-[var(--duration-fast)]",
        "disabled:opacity-40 disabled:cursor-not-allowed disabled:pointer-events-none",
        // O botão "Aprovar" é serifado — instrução preservada do doc 06 original.
        variant === "primary" && size === "lg" ? "font-[family-name:var(--font-display)]" : "",
        SIZE_CLASSES[size],
        SHAPE_CLASSES[shape],
        VARIANT_CLASSES[variant],
        fullWidth ? "w-full" : "",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {/* A largura é preservada durante o loading — nada de botão que encolhe. */}
      {loading ? <Spinner /> : children}
    </button>
  );
}

function Spinner() {
  return (
    <span
      aria-hidden="true"
      className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent"
    />
  );
}
